import { NextRequest, NextResponse } from 'next/server'
import { and, count, eq } from 'drizzle-orm'
import { db } from '@/db'
import {
  KNOWLEDGE_EMBEDDING_MODEL,
  aiArtifacts,
  auditLog,
  knowledgeEntries,
  knowledgeEntryRevisions,
} from '@/db/schema'
import { requireExpertApiUser } from '@/lib/role-guard'
import { buildKnowledgeEmbedding, normalizeKnowledgeInput } from '@/lib/knowledge'

type ProposalPatchBody = {
  action?: unknown
  title?: unknown
  slug?: unknown
  bodyMarkdown?: unknown
}

export async function PATCH(
  request: NextRequest,
  { params }: { params: Promise<{ id: string }> },
) {
  const guard = await requireExpertApiUser()
  if (!guard.ok) return guard.response

  const { id } = await params
  const body = (await request.json().catch(() => null)) as ProposalPatchBody | null

  const [proposal] = await db
    .select()
    .from(aiArtifacts)
    .where(
      and(
        eq(aiArtifacts.id, id),
        eq(aiArtifacts.tenantId, guard.user.tenantId),
        eq(aiArtifacts.artifactType, 'kb_update_proposal'),
      ),
    )
    .limit(1)

  if (!proposal) return NextResponse.json({ error: 'Proposal not found' }, { status: 404 })
  if (proposal.expertAccepted !== null) {
    return NextResponse.json({ error: 'Proposal has already been reviewed' }, { status: 409 })
  }

  const action = typeof body?.action === 'string' ? body.action : ''
  if (action === 'dismiss') {
    await db.update(aiArtifacts).set({ expertAccepted: false }).where(eq(aiArtifacts.id, proposal.id))
    return NextResponse.json({ proposalId: proposal.id, status: 'dismissed' })
  }

  if (action !== 'accept') {
    return NextResponse.json({ error: 'action must be accept or dismiss' }, { status: 400 })
  }

  const parsed = proposal.contentJson ? JSON.parse(proposal.contentJson) as {
    title?: string
    slug?: string
    body_markdown?: string
  } : {}

  const normalized = normalizeKnowledgeInput({
    title: typeof body?.title === 'string' ? body.title : parsed.title ?? '',
    slug: typeof body?.slug === 'string' ? body.slug : parsed.slug,
    kind: 'concept',
    bodyMarkdown: typeof body?.bodyMarkdown === 'string' ? body.bodyMarkdown : parsed.body_markdown ?? '',
  })

  const embedding = buildKnowledgeEmbedding(`${normalized.title}\n${normalized.flatMarkdown}`)
  const [existing] = await db
    .select()
    .from(knowledgeEntries)
    .where(and(eq(knowledgeEntries.tenantId, guard.user.tenantId), eq(knowledgeEntries.slug, normalized.slug)))
    .limit(1)

  let entryId: string
  if (existing) {
    const [entry] = await db
      .update(knowledgeEntries)
      .set({
        title: normalized.title,
        kind: normalized.kind,
        bodyMarkdown: normalized.bodyMarkdown,
        flatMarkdown: normalized.flatMarkdown,
        source: 'llm',
        isVerified: true,
        embedding,
        embeddingModel: KNOWLEDGE_EMBEDDING_MODEL,
        embeddingUpdatedAt: new Date(),
        updatedById: guard.user.id,
        updatedAt: new Date(),
      })
      .where(eq(knowledgeEntries.id, existing.id))
      .returning()
    entryId = entry.id

    const [{ value: revisionCount }] = await db
      .select({ value: count() })
      .from(knowledgeEntryRevisions)
      .where(eq(knowledgeEntryRevisions.entryId, entry.id))

    await db.insert(knowledgeEntryRevisions).values({
      tenantId: guard.user.tenantId,
      entryId,
      revisionNumber: Number(revisionCount) + 1,
      title: entry.title,
      bodyMarkdown: entry.bodyMarkdown,
      flatMarkdown: entry.flatMarkdown,
      editedById: guard.user.id,
      changeReason: 'accepted_kb_update_proposal',
    })
  } else {
    const [entry] = await db
      .insert(knowledgeEntries)
      .values({
        tenantId: guard.user.tenantId,
        slug: normalized.slug,
        title: normalized.title,
        kind: normalized.kind,
        bodyMarkdown: normalized.bodyMarkdown,
        flatMarkdown: normalized.flatMarkdown,
        source: 'llm',
        isVerified: true,
        embedding,
        embeddingModel: KNOWLEDGE_EMBEDDING_MODEL,
        embeddingUpdatedAt: new Date(),
        createdById: guard.user.id,
        updatedById: guard.user.id,
      })
      .returning()
    entryId = entry.id

    await db.insert(knowledgeEntryRevisions).values({
      tenantId: guard.user.tenantId,
      entryId,
      revisionNumber: 1,
      title: entry.title,
      bodyMarkdown: entry.bodyMarkdown,
      flatMarkdown: entry.flatMarkdown,
      editedById: guard.user.id,
      changeReason: 'accepted_kb_update_proposal',
    })
  }

  await db
    .update(aiArtifacts)
    .set({
      expertAccepted: true,
      expertEdited:
        normalized.title !== parsed.title ||
        normalized.slug !== parsed.slug ||
        normalized.bodyMarkdown !== parsed.body_markdown,
      expertVersion: JSON.stringify({
        entryId,
        title: normalized.title,
        slug: normalized.slug,
        body_markdown: normalized.bodyMarkdown,
      }),
    })
    .where(eq(aiArtifacts.id, proposal.id))

  await db.insert(auditLog).values({
    tenantId: guard.user.tenantId,
    actorId: guard.user.id,
    actorType: 'user',
    action: 'knowledge.proposal_accepted',
    entityType: 'knowledge_entry',
    entityId: entryId,
    payload: { proposalId: proposal.id, slug: normalized.slug },
  })

  return NextResponse.json({ proposalId: proposal.id, entryId, status: 'accepted' })
}
